import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { KpiCard } from '../components/KpiCard';
import { StatusBadge } from '../components/StatusBadge';
import { formatIDR, formatDateID } from '../lib/format';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, FileText, Receipt, Wallet, AlertTriangle, CheckCircle2, Phone, Mail, MapPin, Building2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/clients/${id}`);
        setData(data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-40" /><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;
  if (!data) return null;
  const { client, stats, quotations, invoices, payments, followups } = data;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/clients')}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <div className="font-heading text-xl font-semibold">{client.client_name}</div>
          <div className="text-sm text-muted-foreground">{client.company_name || '-'}</div>
        </div>
      </div>

      {/* Contact card */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <Building2 className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div><div className="text-xs text-muted-foreground">PIC</div><div>{client.pic_name || '-'}</div></div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div><div className="text-xs text-muted-foreground">Telepon / WA</div><div>{client.phone_number || '-'}{client.whatsapp_number && client.whatsapp_number !== client.phone_number ? ` / ${client.whatsapp_number}` : ''}</div></div>
          </div>
          <div className="flex items-start gap-3">
            <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div><div className="text-xs text-muted-foreground">Email</div><div>{client.email || '-'}</div></div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div><div className="text-xs text-muted-foreground">Alamat</div><div>{client.address || '-'}</div></div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        <KpiCard label="Total Penawaran" value={String(stats.total_quotations)} icon={FileText} />
        <KpiCard label="Disetujui" value={String(stats.total_approved_quotations)} icon={CheckCircle2} tone="accent" />
        <KpiCard label="Total Invoice" value={String(stats.total_invoices)} icon={Receipt} />
        <KpiCard label="Estimasi Pendapatan" value={stats.total_estimated_income} icon={FileText} tone="primary" compact />
        <KpiCard label="Jumlah Invoice" value={stats.total_invoice_amount} icon={Receipt} compact />
        <KpiCard label="Terbayar" value={stats.total_paid} icon={CheckCircle2} tone="accent" compact />
        <KpiCard label="Outstanding" value={stats.total_outstanding} icon={Wallet} tone="warn" compact />
        <KpiCard label="Overdue" value={stats.total_overdue} icon={AlertTriangle} tone="danger" compact />
      </div>

      {/* Tabs of history */}
      <Tabs defaultValue="quotations">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="quotations">Penawaran ({quotations.length})</TabsTrigger>
          <TabsTrigger value="invoices">Invoice ({invoices.length})</TabsTrigger>
          <TabsTrigger value="payments">Pembayaran ({payments.length})</TabsTrigger>
          <TabsTrigger value="followups">Follow-up ({followups.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="quotations">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>No Penawaran</TableHead><TableHead>Tgl</TableHead><TableHead>Event</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {quotations.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Belum ada penawaran</TableCell></TableRow>}
                  {quotations.map(q => (
                    <TableRow key={q.id} className="cursor-pointer hover:bg-white/4 border-border" onClick={() => navigate(`/quotations/${q.id}`)}>
                      <TableCell className="font-mono-num text-xs">{q.quotation_number}</TableCell>
                      <TableCell>{formatDateID(q.quotation_date)}</TableCell>
                      <TableCell className="text-muted-foreground">{q.event_description}</TableCell>
                      <TableCell className="text-right font-mono-num">{formatIDR(q.total_amount)}</TableCell>
                      <TableCell><StatusBadge status={q.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>No Invoice</TableHead><TableHead>Tgl</TableHead><TableHead>Event</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {invoices.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Belum ada invoice</TableCell></TableRow>}
                  {invoices.map(i => (
                    <TableRow key={i.id} className="cursor-pointer hover:bg-white/4 border-border" onClick={() => navigate(`/invoices/${i.id}`)}>
                      <TableCell className="font-mono-num text-xs">{i.invoice_number}</TableCell>
                      <TableCell>{formatDateID(i.invoice_date)}</TableCell>
                      <TableCell className="text-muted-foreground">{i.event_description}</TableCell>
                      <TableCell className="text-right font-mono-num">{formatIDR(i.invoice_amount)}</TableCell>
                      <TableCell className="text-right font-mono-num">{formatIDR(i.outstanding_amount)}</TableCell>
                      <TableCell><StatusBadge status={i.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>No</TableHead><TableHead>Tgl</TableHead><TableHead>Metode</TableHead><TableHead>Referensi</TableHead><TableHead className="text-right">Jumlah</TableHead></TableRow></TableHeader>
                <TableBody>
                  {payments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Belum ada pembayaran</TableCell></TableRow>}
                  {payments.map(p => (
                    <TableRow key={p.id} className="border-border">
                      <TableCell className="font-mono-num text-xs">{p.payment_number}</TableCell>
                      <TableCell>{formatDateID(p.payment_date)}</TableCell>
                      <TableCell>{p.payment_method}</TableCell>
                      <TableCell className="text-muted-foreground">{p.payment_reference_number || '-'}</TableCell>
                      <TableCell className="text-right font-mono-num">{formatIDR(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="followups">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>Tgl</TableHead><TableHead>Channel</TableHead><TableHead>Hasil</TableHead><TableHead>Follow-up Berikutnya</TableHead></TableRow></TableHeader>
                <TableBody>
                  {followups.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Belum ada follow-up</TableCell></TableRow>}
                  {followups.map(f => (
                    <TableRow key={f.id} className="border-border">
                      <TableCell>{formatDateID(f.follow_up_date)}</TableCell>
                      <TableCell>{f.channel}</TableCell>
                      <TableCell className="text-muted-foreground">{f.follow_up_result || '-'}</TableCell>
                      <TableCell>{formatDateID(f.next_follow_up_date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
